import { api, handleApiError, apiUtils } from './api';
import { logger } from './loggerService';
import {
  AuthResponse,
  LoginCredentials,
  RegisterData,
  RegisterSuccessResponse,
  TokenResponse,
  CurrentUserResponse,
  VerifyEmailPayload,
} from '../types/auth';

// Utility function to get token expiration time
const getTokenExpiration = (token: string): number | null => {
  try {
    const parts = token.split('.');
    if (parts.length !== 3 || !parts[1]) return null;
    
    const payload = JSON.parse(atob(parts[1]));
    return payload.exp * 1000; // Convert to milliseconds
  } catch (_error) {
    return null;
  }
};

const toOptionalString = (value: unknown): string | undefined => {
  if (value === null || value === undefined) return undefined;
  const normalized = String(value).trim();
  return normalized.length > 0 ? normalized : undefined;
};

const toBooleanish = (value: unknown): boolean => {
  if (typeof value === 'boolean') return value;
  if (typeof value === 'number') return value === 1;
  if (typeof value === 'string') {
    const normalized = value.trim().toLowerCase();
    if (normalized === 'true' || normalized === '1' || normalized === 'active') return true;
    if (normalized === 'false' || normalized === '0' || normalized === 'inactive') return false;
  }
  return Boolean(value);
};

const normalizeUser = (rawUser: any) => {
  const memberships = Array.isArray(rawUser?.ca_firm_memberships) ? rawUser.ca_firm_memberships : [];
  const isCA = rawUser?.account_type === 'ca';

  const mapMembership = (membership: any) => ({
    ca_firm_id: String(
      membership?.ca_firm_id ??
        membership?.firm_id ??
        membership?.caFirmId ??
        membership?.firmId ??
        membership?.ca_firm?.id ??
        membership?.firm?.id ??
        membership?.id ??
        ''
    ),
    ca_firm_name: String(membership?.ca_firm_name ?? membership?.firm_name ?? ''),
    role: String(membership?.role ?? ''),
    is_active: toBooleanish(membership?.is_active ?? membership?.active ?? membership?.status),
    organization_id: toOptionalString(
      membership?.organization_id ?? membership?.organizationId ?? membership?.org_id ?? membership?.orgId
    ),
    tenant_organization_id: toOptionalString(
      membership?.tenant_organization_id ??
        membership?.tenantOrganizationId ??
        membership?.tenant_org_id ??
        membership?.tenantOrgId
    ),
    workspace_organization_id: toOptionalString(
      membership?.workspace_organization_id ??
        membership?.workspaceOrganizationId ??
        membership?.workspace_org_id ??
        membership?.workspaceOrgId
    ),
  });

  let ca_firm_memberships = memberships.map(mapMembership);

  const explicitlyNeedsCAWorkspaceSetup = rawUser?.needs_ca_workspace_setup === true;

  if (isCA && ca_firm_memberships.length === 0 && !explicitlyNeedsCAWorkspaceSetup) {
    const soleFirm = String(
      rawUser?.ca_firm_id ??
        rawUser?.firm_id ??
        rawUser?.caFirmId ??
        rawUser?.firmId ??
        rawUser?.ca_firm?.id ??
        rawUser?.firm?.id ??
        ''
    ).trim();
    if (soleFirm) {
      ca_firm_memberships = [
        {
          ca_firm_id: soleFirm,
          ca_firm_name: String(rawUser?.ca_firm_name ?? ''),
          role: String(rawUser?.ca_membership_role ?? 'member'),
          is_active: true,
        },
      ];
    }
  }

  return {
    ...rawUser,
    account_type: isCA ? 'ca' : 'business',
    ca_firm_memberships,
    ...(rawUser?.needs_ca_workspace_setup !== undefined && rawUser?.needs_ca_workspace_setup !== null
      ? { needs_ca_workspace_setup: toBooleanish(rawUser.needs_ca_workspace_setup) }
      : {}),
  };
};

const normalizeAuthResponse = (rawData: any): AuthResponse => ({
  ...rawData,
  user: normalizeUser(rawData?.user ?? {}),
});

const normalizeCurrentUserResponse = (rawData: any): CurrentUserResponse => ({
  ...rawData,
  user: normalizeUser(rawData?.user ?? {}),
});

/**
 * Authentication service for handling user login, registration, and token management
 */
export const authService = {
  /**
   * Authenticate user with email and password
   * @param credentials - User login credentials
   * @returns Promise<AuthResponse> - Authentication response with tokens and user data
   */
  async login(credentials: LoginCredentials): Promise<AuthResponse> {
    try {
      const response = await api.post('/auth/login', credentials);
      logger.info('User login successful', 'AuthService.login', { email: credentials.email });
      return normalizeAuthResponse(response.data?.data ?? {});
    } catch (error) {
      return handleApiError(error, 'AuthService.login');
    }
  },

  /**
   * Register a new user account. When verification is required, no tokens are returned.
   */
  async register(userData: RegisterData): Promise<RegisterSuccessResponse> {
    try {
      const response = await api.post('/auth/register', userData);
      logger.info('User registration successful', 'AuthService.register', { email: userData.email });
      const data = response.data?.data ?? {};
      return {
        verification_required: data.verification_required === true,
        user: data.user,
        message: data.message ?? response.data?.message ?? '',
      };
    } catch (error) {
      return handleApiError(error, 'AuthService.register');
    }
  },

  /**
   * Confirm signup with the 6-digit code emailed to the user. Returns tokens like login.
   */
  async verifyEmail(payload: VerifyEmailPayload): Promise<AuthResponse> {
    try {
      const response = await api.post('/auth/verify-email', payload);
      logger.info('Email verified', 'AuthService.verifyEmail', { email: payload.email });
      return normalizeAuthResponse(response.data?.data ?? {});
    } catch (error) {
      return handleApiError(error, 'AuthService.verifyEmail');
    }
  },

  /**
   * Resend signup verification code. Backend response is generic; use a generic UI message.
   */
  async resendVerification(email: string): Promise<void> {
    try {
      await api.post('/auth/resend-verification', { email });
      logger.info('Verification resend requested', 'AuthService.resendVerification', { email });
    } catch (error) {
      return handleApiError(error, 'AuthService.resendVerification');
    }
  },

  /**
   * Refresh authentication token
   * @param refreshToken - Current refresh token
   * @returns Promise<TokenResponse> - New token pair
   */
  async refreshToken(refreshToken: string): Promise<TokenResponse> {
    try {
      const response = await api.post('/auth/refresh', {
        refresh_token: refreshToken,
      });
      logger.debug('Token refresh successful', 'AuthService.refreshToken');
      return response.data.data;
    } catch (error) {
      return handleApiError(error, 'AuthService.refreshToken');
    }
  },

  /**
   * Get current user information
   * @returns Promise<CurrentUserResponse> - Current user data
   */
  async getCurrentUser(): Promise<CurrentUserResponse> {
    try {
      const response = await api.get('/auth/me');
      logger.debug('Current user data fetched', 'AuthService.getCurrentUser');
      return normalizeCurrentUserResponse(response.data?.data ?? {});
    } catch (error) {
      return handleApiError(error, 'AuthService.getCurrentUser');
    }
  },

  /**
   * Logout user and clear tokens
   * @returns Promise<void>
   */
  async logout(): Promise<void> {
    try {
      await api.post('/auth/logout');
      logger.info('User logout successful', 'AuthService.logout');
    } catch (error) {
      logger.warn('Logout API call failed, but proceeding with local cleanup', 'AuthService.logout', error);
    } finally {
      // Always clear local storage regardless of API response
      localStorage.removeItem('access_token');
      localStorage.removeItem('refresh_token');
      logger.info('Local storage cleared', 'AuthService.logout');
    }
  },

  /**
   * Check if user is authenticated
   * @returns boolean - True if user has valid token
   */
  isAuthenticated(): boolean {
    const token = localStorage.getItem('access_token');
    if (!token) return false;
    
    return !apiUtils.isTokenExpired(token);
  },

  /**
   * Get stored access token
   * @returns string | null - Access token or null if not found/expired
   */
  getAccessToken(): string | null {
    const token = localStorage.getItem('access_token');
    if (!token || apiUtils.isTokenExpired(token)) {
      return null;
    }
    return token;
  },

  /**
   * Get stored refresh token
   * @returns string | null - Refresh token or null if not found
   */
  getRefreshToken(): string | null {
    return localStorage.getItem('refresh_token');
  },

  /**
   * Store authentication tokens
   * @param accessToken - Access token to store
   * @param refreshToken - Refresh token to store
   */
  setTokens(accessToken: string, refreshToken: string): void {
    localStorage.setItem('access_token', accessToken);
    localStorage.setItem('refresh_token', refreshToken);
    logger.debug('Tokens stored in localStorage', 'AuthService.setTokens');
  },

  /**
   * Clear all stored tokens
   */
  clearTokens(): void {
    localStorage.removeItem('access_token');
    localStorage.removeItem('refresh_token');
    logger.debug('Tokens cleared from localStorage', 'AuthService.clearTokens');
  },

  /**
   * Get token expiration time
   * @param token - Token to check
   * @returns number | null - Expiration timestamp in milliseconds
   */
  getTokenExpiration(token: string): number | null {
    return getTokenExpiration(token);
  },

  /**
   * Check if token will expire soon (within 1 hour)
   * @param token - Token to check
   * @returns boolean - True if token expires soon
   */
  isTokenExpiringSoon(token: string): boolean {
    const expiration = getTokenExpiration(token);
    if (!expiration) return true;
    
    // Check if token expires within 1 hour (for 24-hour tokens)
    const oneHourFromNow = Date.now() + (60 * 60 * 1000);
    return expiration < oneHourFromNow;
  },

  /**
   * Switch to a different organization
   * @param organizationId - ID of organization to switch to
   * @returns Promise<void>
   */
  async switchOrganization(organizationId: string): Promise<void> {
    try {
      await api.post(`/organizations/${organizationId}/switch`);
      logger.info('Organization switch successful', 'AuthService.switchOrganization', { organizationId });
    } catch (error) {
      return handleApiError(error, 'AuthService.switchOrganization');
    }
  },

  /**
   * Change user password
   * @param currentPassword - Current password for verification
   * @param newPassword - New password
   * @returns Promise<void>
   */
  async changePassword(currentPassword: string, newPassword: string): Promise<void> {
    try {
      const response = await api.post('/auth/change-password', {
        current_password: currentPassword,
        new_password: newPassword
      });
      logger.info('Password changed successfully', 'AuthService.changePassword');
      return response.data;
    } catch (error) {
      return handleApiError(error, 'AuthService.changePassword');
    }
  },

  /**
   * Request a password-reset email for the given address.
   * TODO(backend): Implement POST /auth/forgot-password with JSON body `{ email }` (or align payload/path with your API).
   */
  async requestPasswordReset(email: string): Promise<void> {
    try {
      await api.post('/auth/forgot-password', { email });
      logger.info('Password reset email requested', 'AuthService.requestPasswordReset', {
        email,
      });
    } catch (error) {
      return handleApiError(error, 'AuthService.requestPasswordReset');
    }
  },

  /**
   * Complete password reset using the token from the email link (`/reset-password?token=...`).
   * TODO(backend): Implement POST /auth/reset-password with JSON body `{ token, new_password }` (or align with your API).
   */
  async resetPasswordWithToken(token: string, newPassword: string): Promise<void> {
    try {
      await api.post('/auth/reset-password', {
        token,
        new_password: newPassword,
      });
      logger.info('Password reset completed', 'AuthService.resetPasswordWithToken');
    } catch (error) {
      return handleApiError(error, 'AuthService.resetPasswordWithToken');
    }
  },
};